import { useEffect, useState, type MouseEvent } from "react";
import { useHealth } from "../features/health/useHealth";
import { StatusPill } from "../components/StatusPill";
import { FlowPreview } from "../features/flow/FlowPreview";
import { useAuth } from "../features/auth/useAuth";
import { SignIn } from "../features/auth/SignIn";
import { AcceptInvite } from "../features/auth/AcceptInvite";
import { ResetPassword } from "../features/auth/ResetPassword";
import { ForgotPassword } from "../features/auth/ForgotPassword";
import { MyAccountPanel } from "../features/auth/MyAccountPanel";
import { InvitesPanel } from "../features/invites/InvitesPanel";
import { PasswordResetPanel } from "../features/invites/PasswordResetPanel";
import { ReferencesPanel } from "../features/flow/ReferencesPanel";
import { AdhocCallPanel } from "../features/flow/AdhocCallPanel";
import { CallResultsPanel } from "../features/flow/CallResultsPanel";
import { FlowsListPanel } from "../features/flow/FlowsListPanel";
import { DeveloperPanel } from "../features/developer/DeveloperPanel";
import { SendMsgApiPanel } from "../features/developer/SendMsgApiPanel";
import { Sidebar, isPlainLeftClick, type SidebarGroup, type SidebarItem } from "./Sidebar";
import { Topbar } from "./Topbar";
import { AuthLayout } from "../features/auth/AuthLayout";
import { Icon, type IconName } from "../components/Icon";
import { pathForTab, tabFromPath, type TabId } from "./tabRoutes";
import type { Identity } from "../api/client";

/**
 * Application shell.
 *
 * A real sign-in screen gates everything below it (P7): there is no way to
 * see the dashboard without an authenticated session, and the identity shown
 * always comes from the API, never from anything stored client-side.
 *
 * There is no client-side router dependency in this app: dashboard sections
 * map to fixed paths through the History API (tabRoutes.ts). An invite or
 * reset link is a plain URL (`/accept-invite?token=...`, `/reset-password?token=...`) that a
 * real deployment would email; reading `window.location` directly for these
 * fixed paths is a reasonable trade-off for a handful of screens rather than
 * pulling in routing for the whole app. `/forgot-password` needs no token —
 * it is the public form that REQUESTS a reset link, not one that consumes
 * one. All three render regardless of auth state, since accepting an invite
 * creates a DIFFERENT, new user.
 */
export function App() {
  const path = window.location.pathname;
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  if (path === "/accept-invite" && token) {
    return <AcceptInvite token={token} />;
  }
  if (path === "/reset-password" && token) {
    return <ResetPassword token={token} />;
  }
  if (path === "/forgot-password") {
    return <ForgotPassword />;
  }

  return <AuthGate />;
}

function AuthGate() {
  const { status, identity, login, logout } = useAuth();

  if (status === "checking") {
    return (
      <AuthLayout>
        <p className="shell__subtitle">Checking session…</p>
      </AuthLayout>
    );
  }

  if (status === "signed_out" || !identity) {
    return <SignIn onSubmit={login} />;
  }

  return <Dashboard identity={identity} onSignOut={() => void logout()} />;
}

/** Matches the CSS breakpoint below which the sidebar becomes a drawer. */
function isMobileViewport(): boolean {
  return window.matchMedia("(max-width: 1023px)").matches;
}

const BASE_TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "flows", label: "Flow designer (preview)" },
];

function Dashboard({ identity, onSignOut }: { identity: Identity; onSignOut: () => void }) {
  const { snapshot, refresh } = useHealth();
  const [activeTab, setActiveTab] = useState<TabId>(() => tabFromPath(window.location.pathname));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Results and Team both require tenant_admin on the backend
  // (ActionPlaceAdhocCall / tenant-admin-only invites) — gated the same way
  // here so the tab itself is never shown to a role that would just get a
  // 403 from it.
  const isAdmin = identity.role === "tenant_admin";
  const isViewer = identity.role === "viewer";
  const tabs = [
    ...BASE_TABS,
    // GET /v1/flows is gated server-side on ActionManageFlows
    // (operator/tenant_admin, not viewer) — mirrored here so the tab is
    // never shown to a role that would just get a 403 from it.
    ...(!isViewer ? [{ id: "flows-list" as const, label: "Templates" }] : []),
    ...(isAdmin
      ? [
          // POST /v1/adhoc-calls is tenant_admin-only (ActionPlaceAdhocCall).
          { id: "test-call" as const, label: "Test call" },
          { id: "results" as const, label: "Results" },
          { id: "team" as const, label: "Team" },
          { id: "developer" as const, label: "API keys" },
          { id: "developer-api" as const, label: "API" },
        ]
      : []),
  ];
  const tab = tabs.some((t) => t.id === activeTab) ? activeTab : "overview";
  const tabLabel = tabs.find((t) => t.id === tab)?.label ?? "Overview";

  // Each section has its own URL (tabRoutes.ts). Clicking a section pushes a
  // history entry; back/forward restores the section from the URL.
  const selectTab = (next: TabId) => {
    setActiveTab(next);
    if (window.location.pathname !== pathForTab(next)) {
      window.history.pushState(null, "", pathForTab(next));
    }
  };

  useEffect(() => {
    const onPopState = () => setActiveTab(tabFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // An unknown path, or a section this role can't open, renders the overview;
  // correct the address bar to match without adding a history entry.
  useEffect(() => {
    if (window.location.pathname !== pathForTab(tab)) {
      window.history.replaceState(null, "", pathForTab(tab));
    }
    document.title = `${tabLabel} | Utility Voice IVR`;
  }, [tab, tabLabel]);

  // One menu button drives both sidebar modes: the icon-only rail on
  // desktop, the off-canvas drawer on narrow viewports.
  const toggleSidebar = () => {
    if (isMobileViewport()) {
      setMobileOpen((open) => !open);
    } else {
      setCollapsed((value) => !value);
    }
  };

  const navItem = (id: TabId, label: string, icon: IconName): SidebarItem => ({
    key: id,
    label,
    icon,
    href: pathForTab(id),
    active: tab === id,
    onSelect: () => selectTab(id),
  });

  const groups: SidebarGroup[] = [
    { items: [navItem("overview", "Overview", "grid")] },
    {
      label: "Build",
      items: [
        navItem("flows", "Flow designer (preview)", "flow"),
        ...(!isViewer ? [navItem("flows-list", "Templates", "list")] : []),
        ...(isAdmin ? [navItem("test-call", "Test call", "phone")] : []),
      ],
    },
    ...(isAdmin
      ? [
          { label: "Operate", items: [navItem("results", "Results", "chart")] },
          { label: "Team", items: [navItem("team", "Team", "users")] },
          {
            label: "Developer",
            items: [navItem("developer", "API keys", "key"), navItem("developer-api", "API", "code")],
          },
        ]
      : []),
  ];

  const brand = (
    <div className="sidebar__brand-inner">
      <span className="brand-mark" aria-hidden="true">
        <Icon name="phone" />
      </span>
      <div className="sidebar__brand-text">
        <p className="sidebar__brand-title">Utility Voice IVR</p>
        <p className="sidebar__brand-subtitle">
          {snapshot.version ? `Build ${snapshot.version}` : "Control plane"}
        </p>
      </div>
    </div>
  );

  return (
    <div className={`dashboard--shell${collapsed ? " dashboard--shell-collapsed" : ""}`}>
      <Sidebar
        groups={groups}
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        brand={brand}
        filter={search}
      />
      <div className="dashboard__content">
        <Topbar
          identity={identity}
          onSignOut={onSignOut}
          searchValue={search}
          onSearchChange={setSearch}
          onToggleSidebar={toggleSidebar}
          sidebarExpanded={isMobileViewport() ? mobileOpen : !collapsed}
        />

        <main className="page">
          <div className="page__header">
            <h1 className="page__title">{tabLabel}</h1>
            <nav className="page__breadcrumb" aria-label="Breadcrumb">
              <a
                href={pathForTab("overview")}
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  if (isPlainLeftClick(event)) {
                    event.preventDefault();
                    selectTab("overview");
                  }
                }}
              >
                Home
              </a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{tabLabel}</span>
            </nav>
          </div>

          {tab === "overview" ? (
            <section aria-labelledby="readiness-heading" className="card">
              <div className="card__header">
                <div>
                  <h2 id="readiness-heading">Readiness</h2>
                  <p className="card__subtitle">
                    {snapshot.lastCheckedAt
                      ? `Last checked ${snapshot.lastCheckedAt.toLocaleTimeString()}`
                      : "Checking the API, database and media plane"}
                  </p>
                </div>
                <button type="button" className="btn--outline" onClick={refresh}>
                  Check now
                </button>
              </div>
              <div className="stat-grid">
                <div className="stat-card">
                  <span className="stat-card__icon">
                    <Icon name="server" size={24} />
                  </span>
                  <div className="stat-card__body">
                    <p className="stat-card__label">API process</p>
                    <StatusPill state={snapshot.api} />
                  </div>
                </div>
                <div className="stat-card">
                  <span className="stat-card__icon">
                    <Icon name="database" size={24} />
                  </span>
                  <div className="stat-card__body">
                    <p className="stat-card__label">Control plane (database)</p>
                    <StatusPill state={snapshot.control} />
                  </div>
                </div>
                <div className="stat-card">
                  <span className="stat-card__icon">
                    <Icon name="phone" size={24} />
                  </span>
                  <div className="stat-card__body">
                    <p className="stat-card__label">Media plane</p>
                    <StatusPill state={snapshot.media} />
                  </div>
                  {snapshot.mediaPlane ? (
                    <span className="stat-card__detail">
                      {snapshot.mediaPlane} · {snapshot.adapter} · {snapshot.instanceId}
                    </span>
                  ) : null}
                </div>
              </div>
              {snapshot.lastError ? <p className="card__error">Last error: {snapshot.lastError}</p> : null}
              <p className="card__note">
                Media readiness is separate from web readiness on purpose: a node that cannot
                drive calls stops admitting them without taking the API offline.
              </p>
            </section>
          ) : null}

          {tab === "overview" ? <MyAccountPanel currentEmail={identity.email} /> : null}

          {tab === "flows" ? (
            <>
              {identity.role !== "viewer" ? <ReferencesPanel /> : null}
              <FlowPreview canPlaceTestCall={isAdmin} />
            </>
          ) : null}

          {tab === "test-call" && isAdmin ? (
            <>
              {/* The call form first, the reference registry below it: the
                  form is what this tab is for, and the registry is the
                  lookup you reach for only when the form rejects a voice or
                  asset you tried to use. */}
              <AdhocCallPanel />
              <ReferencesPanel />
            </>
          ) : null}

          {tab === "flows-list" && !isViewer ? <FlowsListPanel canPlaceTestCall={isAdmin} /> : null}

          {tab === "developer" && isAdmin ? <DeveloperPanel /> : null}

          {tab === "developer-api" && isAdmin ? <SendMsgApiPanel /> : null}

          {tab === "results" && identity.role === "tenant_admin" ? <CallResultsPanel /> : null}

          {tab === "team" && identity.role === "tenant_admin" ? (
            <>
              <InvitesPanel />
              <PasswordResetPanel />
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}
